export type ImportCttField = {
  label?: string;
  Label?: string;
  fieldElementName?: string;
  FieldElementName?: string;
};

export function fieldKey(field: ImportCttField | null | undefined): string {
  return (field?.label ?? field?.Label ?? "").trim();
}

export function fieldElementName(field: ImportCttField | null | undefined): string {
  return (field?.fieldElementName ?? field?.FieldElementName ?? "").trim();
}

export function getRowFieldValue(
  row: Record<string, unknown> | null | undefined,
  fieldName: string,
): string {
  if (!row || !fieldName) return "";
  if (Object.prototype.hasOwnProperty.call(row, fieldName)) {
    const value = row[fieldName];
    return value == null ? "" : String(value);
  }
  const key = Object.keys(row).find((k) => k.toLowerCase() === fieldName.toLowerCase());
  if (!key) return "";
  const value = row[key];
  return value == null ? "" : String(value);
}

export function formatTsvCell(value: unknown): string {
  return (value ?? "")
    .toString()
    .replace(/\t/g, " ")
    .replace(/\n/g, " ")
    .replace(/\r/g, "");
}

export function buildTsvRow(values: unknown[]): string {
  return values.map((value) => formatTsvCell(value)).join("\t");
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

export function findCttFieldByCanonicalName(
  cttFields: ImportCttField[],
  canonicalName: string,
): ImportCttField | undefined {
  const wanted = normalizeName(canonicalName);
  return cttFields.find((field) => {
    if (normalizeName(fieldElementName(field)) === wanted) return true;
    return normalizeName(fieldKey(field)) === wanted;
  });
}

/** Tab-separated field labels so Excel paste creates the import columns. */
export function buildColumnHeaderTsv(cttFields: ImportCttField[]): string {
  return buildTsvRow(cttFields.map(fieldKey));
}

/** Post-import clipboard: first name, last name, and test URL only. */
export function buildImportResultClipboardTsv(options: {
  tests: Array<Record<string, unknown>>;
  importedRows: Array<Record<string, unknown>>;
  cttFields: ImportCttField[];
  testUrlForCode: (code: string) => string;
}): string {
  const { tests, importedRows, cttFields, testUrlForCode } = options;
  const firstNameField = findCttFieldByCanonicalName(cttFields, "First Name");
  const lastNameField = findCttFieldByCanonicalName(cttFields, "Last Name");
  const firstHeader = firstNameField ? fieldKey(firstNameField) : "First Name";
  const lastHeader = lastNameField ? fieldKey(lastNameField) : "Last Name";
  const headers = [firstHeader, lastHeader, "Test URL"];

  const dataRows = tests.map((test, rowIndex) => {
    const importedRow = importedRows[rowIndex] || {};
    const firstName =
      getRowFieldValue(importedRow, firstHeader) || getRowFieldValue(test, firstHeader);
    const lastName =
      getRowFieldValue(importedRow, lastHeader) || getRowFieldValue(test, lastHeader);
    const url = testUrlForCode(getRowFieldValue(test, "Code"));
    return [firstName, lastName, url];
  });

  return [headers, ...dataRows].map((row) => buildTsvRow(row)).join("\r\n");
}
